import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import AdminShell from "../../components/AdminShell";

export default function AdminFinancials() {
  const [applicants, setApplicants] = useState([]);
  const [fees, setFees] = useState({ phase1Fee: 6000, phase2Fee: 6000 });
  const [editFees, setEditFees] = useState(false);
  const [feeDraft, setFeeDraft] = useState({ phase1Fee: 6000, phase2Fee: 6000 });
  const [filter, setFilter] = useState("all");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const feeSnap = await getDoc(doc(db, "fees", "config"));
        if (feeSnap.exists()) { setFees(feeSnap.data()); setFeeDraft(feeSnap.data()); }
        const snap = await getDocs(collection(db, "applicants"));
        setApplicants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
    })();
  }, []);

  const saveFees = async () => {
    try {
      await setDoc(doc(db, "fees", "config"), { ...feeDraft, updatedAt: serverTimestamp() });
      setFees(feeDraft); setEditFees(false); setMsg("Fees updated.");
    } catch (e) { setMsg("Error: " + e.message); }
  };

  const logs = [];
  applicants.forEach((a) => {
    if (a.paymentStatus) {
      logs.push({
        applicantId: a.applicationId,
        name: `${a.firstName} ${a.lastName}`,
        phase: "Phase 1 - COE & Endorsement",
        status: a.paymentStatus,
        amount: fees.phase1Fee,
        date: a.updatedAt?.toDate?.() || null,
        firestoreId: a.id
      });
    }
    if (a.phase2Payment?.status) {
      logs.push({
        applicantId: a.applicationId,
        name: `${a.firstName} ${a.lastName}`,
        phase: "Phase 2 - Company Profile & Brochure",
        status: a.phase2Payment.status,
        amount: fees.phase2Fee,
        date: a.phase2Payment.date ? new Date(a.phase2Payment.date) : null,
        firestoreId: a.id
      });
    }
  });

  const filtered = filter === "all" ? logs : logs.filter((l) => l.status === filter);
  const totalCollected = logs.filter((l) => l.status === "collected").reduce((sum, l) => sum + (l.amount || 0), 0);
  const collectedCount = logs.filter((l) => l.status === "collected").length;
  const pendingCount = logs.filter((l) => l.status !== "collected").length;
  const statusColor = (s) => s === "collected" ? "badge-accepted" : "badge-pending";

  return (
    <AdminShell title="Financials">
      {msg && <div className="alert alert-info">{msg}</div>}

      <div className="card mb-24">
        <div className="flex-between mb-16">
          <h3 style={{ fontSize: 16 }}><i className="ti ti-settings"></i> Fee Configuration</h3>
          {!editFees
            ? <button className="btn btn-outline" onClick={() => { setEditFees(true); setFeeDraft(fees); }}><i className="ti ti-edit"></i> Edit Fees</button>
            : <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary" onClick={saveFees}><i className="ti ti-check"></i> Save</button>
                <button className="btn btn-outline" onClick={() => setEditFees(false)}>Cancel</button>
              </div>
          }
        </div>
        <div className="form-row cols-2">
          <div className="form-group">
            <label className="form-label">Phase 1 Fee - COE &amp; Endorsement Letter</label>
            {editFees
              ? <input type="number" className="form-control" value={feeDraft.phase1Fee} onChange={(e) => setFeeDraft({ ...feeDraft, phase1Fee: parseInt(e.target.value) || 0 })} />
              : <div style={{ fontSize: 24, fontWeight: 700, color: "var(--gold-600)", fontFamily: "var(--font-mono)" }}>PHP {fees.phase1Fee?.toLocaleString()}</div>
            }
          </div>
          <div className="form-group">
            <label className="form-label">Phase 2 Fee - Company Profile, Letterhead &amp; Brochure</label>
            {editFees
              ? <input type="number" className="form-control" value={feeDraft.phase2Fee} onChange={(e) => setFeeDraft({ ...feeDraft, phase2Fee: parseInt(e.target.value) || 0 })} />
              : <div style={{ fontSize: 24, fontWeight: 700, color: "var(--gold-600)", fontFamily: "var(--font-mono)" }}>PHP {fees.phase2Fee?.toLocaleString()}</div>
            }
          </div>
        </div>
      </div>

      <div className="stats-grid mb-24">
        {[
          { label: "Total Collected", value: `PHP ${totalCollected.toLocaleString()}` },
          { label: "Total Transactions", value: logs.length },
          { label: "Collected", value: collectedCount },
          { label: "Pending Collection", value: pendingCount },
        ].map((c) => (
          <div key={c.label} className="stat-card">
            <div className="stat-label">{c.label}</div>
            <div className="stat-value">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="flex-between mb-16" style={{ flexWrap: "wrap", gap: 12 }}>
          <h3 style={{ fontSize: 16 }}><i className="ti ti-list"></i> Payment Log</h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["all","All"],["collected","Collected"],["pending_collection","Pending"]].map(([v, l]) => (
              <button key={v} className={"btn " + (filter === v ? "btn-primary" : "btn-outline")} style={{ fontSize: 12, padding: "4px 10px" }} onClick={() => setFilter(v)}>{l}</button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="empty-state"><i className="ti ti-receipt-off"></i><div>No payment records found</div></div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Applicant</th><th>Phase</th><th>Amount</th><th>Status</th><th>Date</th></tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{l.name}</div>
                      <div className="mono text-muted" style={{ fontSize: 11 }}>{l.applicantId}</div>
                    </td>
                    <td style={{ fontSize: 13 }}>{l.phase}</td>
                    <td style={{ fontWeight: 700, fontFamily: "var(--font-mono)" }}>PHP {(l.amount || 0).toLocaleString()}</td>
                    <td><span className={`badge ${statusColor(l.status)}`}>{l.status?.replace(/_/g, " ")}</span></td>
                    <td style={{ fontSize: 12 }}>{l.date ? l.date.toLocaleDateString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
